import { useCallback, useEffect, useMemo, useState } from 'react';
import enums from '@happylife-a/enums';
import * as orderHooks from '../core/hooks/orderHooks';
import * as shoppingCartHooks from '../core/hooks/shoppingCartHooks';
import { useQueryClient } from '@tanstack/react-query';

const typesWithPickupAddress = [enums.order.OrderShippingType.Pickup];
const typesWithDeliveryAddress = [
  enums.order.OrderShippingType.Delivery,
  enums.order.OrderShippingType.Express,
];

function useCalculateShoppingCost(
  {
    shippingType,
    productDetailIds,
    productDetailIdsWithQuantities,
    pickupAddressId,
    customerPickupAddressId,
    deliveryAddressId,
    paymentMethodId,
  },
  isShoppingCart = true
) {
  const queryClient = useQueryClient();

  const commonProps = {
    shippingType: shippingType,
    paymentMethodId: paymentMethodId,
  };

  if (typesWithPickupAddress.includes(commonProps.shippingType)) {
    commonProps.customerPickupAddressId = customerPickupAddressId;
    commonProps.pickupAddressId = pickupAddressId;
  } else if (typesWithDeliveryAddress.includes(commonProps.shippingType)) {
    commonProps.deliveryAddressId = deliveryAddressId;
  }

  const [error, setError] = useState(null);
  const calculationShoppingCart = shoppingCartHooks.useCalculateCostAndDate(
    {
      ...commonProps,
      productDetailIds: productDetailIds || [],
    },
    { enabled: false }
  );
  const calculationOrder = orderHooks.useCalculateCostAndDate(
    {
      ...commonProps,
      productDetailIdsWithQuantities: productDetailIdsWithQuantities,
    },
    { enabled: false }
  );

  const calculation = isShoppingCart ? calculationShoppingCart : calculationOrder;

  const reload = useCallback(async () => {
    const isNotEmpty = Object.values(commonProps).some(Boolean);
    if (!isNotEmpty) {
      return;
    }

    if (
      (commonProps.shippingType === enums.order.OrderShippingType.Pickup &&
        !commonProps.customerPickupAddressId &&
        !commonProps.pickupAddressId) ||
      (commonProps.shippingType === enums.order.OrderShippingType.Delivery &&
        !commonProps.deliveryAddressId)
    ) {
      return;
    }

    await queryClient.cancelQueries({ queryKey: calculation.queryKey });
    return calculation.refetch();
  }, [commonProps]);

  useEffect(() => {
    let isValidAddressSelected = true;
    const addressType = shippingType
      ? enums.order.OrderAddressTypeByShippingType[shippingType]
      : null;

    if (shippingType) {
      if (
        addressType === enums.order.OrderAddressType.PickupAddress &&
        !pickupAddressId &&
        !customerPickupAddressId
      ) {
        isValidAddressSelected = false;
      }
      if (addressType === enums.order.OrderAddressType.DeliveryAddress && !deliveryAddressId) {
        isValidAddressSelected = false;
      }
    } else {
      if (!pickupAddressId && !deliveryAddressId && !customerPickupAddressId) {
        isValidAddressSelected = false;
      }
    }

    if (!isValidAddressSelected) {
      if (addressType === enums.order.OrderAddressType.PickupAddress) {
        setError('No pickup address chosen.');
      } else if (addressType === enums.order.OrderAddressType.DeliveryAddress) {
        setError('No delivery address chosen.');
      } else {
        setError('No pickup or delivery address chosen.');
      }
    } else {
      setError(null);
    }

    if (!shippingType) {
      return;
    }

    reload();
  }, [
    productDetailIds,
    productDetailIdsWithQuantities,
    pickupAddressId,
    customerPickupAddressId,
    deliveryAddressId,
    paymentMethodId,
    shippingType,
  ]);

  return useMemo(() => {
    const isHttpError = !!calculation.error;
    let errorMessage = error;
    if (isHttpError) {
      errorMessage = 'Failed to calculate cost via third-party provider.';
    }

    return {
      isHttpError: isHttpError,
      error: errorMessage,
      isLoading: calculation.isLoading || calculation.isFetching,
      response: calculation.data || {},
      refetch: reload,
    };
  }, [calculation.isLoading, calculation.isFetching, calculation.data, calculation.error, error]);
}

export default useCalculateShoppingCost;
